import React, { useState, useEffect, useRef, useMemo } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Icon,
    useColorModeValue,
    IconButton,
    Button,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Tag,
    TagLabel,
    TagCloseButton,
    useDisclosure,
    Textarea,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Tooltip,
    Divider,
    Grid,
    GridItem,
    Code,
    Badge,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Portal,
} from "@chakra-ui/react";
import {
    FiMove, FiX, FiRefreshCw, FiChevronRight, FiMap, FiDatabase,
    FiSettings, FiPlay, FiCpu, FiCode, FiType, FiPlus, FiHelpCircle,
    FiClock, FiHash, FiScissors, FiAlignCenter, FiRepeat, FiZap,
    FiSearch, FiEye, FiEyeOff
} from "react-icons/fi";

interface SourceLink {
    field: string;
}

interface PipelineStep {
    id: string;
    type: 'UPPER' | 'LOWER' | 'TITLE' | 'TRIM' | 'SPLIT' | 'CONCAT' | 'MATH' | 'CONSTANT' | 'DATE_FORMAT' | 'SUBSTRING' | 'REPLACE' | 'ADDRESS';
    params: any;
}

interface TargetMapping {
    sources: SourceLink[];
    pipeline: PipelineStep[];
}

interface Mapping {
    [key: string]: TargetMapping;
}

interface FieldMappingToolProps {
    mapping?: any;
    targetFields: string[];
    sampleData?: any;
    apiName: string;
    sampleSource: string;
    availableApis: string[];
    onChange: (mapping: Mapping) => void;
    onSampleSourceChange: (source: "mock" | "online_static" | "online_live" | "local_static" | "local_live") => void;
    onApiChange: (api: string) => void;
}

const FieldMappingTool: React.FC<FieldMappingToolProps> = ({
    mapping = {},
    targetFields = [],
    sampleData = {},
    apiName,
    sampleSource,
    availableApis,
    onChange,
    onSampleSourceChange,
    onApiChange
}) => {
    const Trigger = PopoverTrigger as any;
    // Normalization Layer
    const normalizedMapping = useMemo(() => {
        const result: Mapping = {};

        // First, ensure every target field has a default entry
        targetFields.forEach(field => {
            result[field] = { sources: [], pipeline: [] };
        });

        // Then, overlay the existing mapping
        Object.entries(mapping || {}).forEach(([key, val]) => {
            if (typeof val === 'string') {
                result[key] = { sources: [{ field: val }], pipeline: [] };
            } else if (val && typeof val === 'object' && ('sources' in (val as any) || 'pipeline' in (val as any))) {
                result[key] = {
                    sources: (val as any).sources || [],
                    pipeline: (val as any).pipeline || []
                };
            }
        });
        return result;
    }, [mapping, targetFields]);

    const [availableFields, setAvailableFields] = useState<string[]>([]);
    const [draggingField, setDraggingField] = useState<string | null>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [lines, setLines] = useState<any[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [isTesterOpen, setIsTesterOpen] = useState(false);
    const [activeStepId, setActiveStepId] = useState<string | null>(null);
    const [manualStepInput, setManualStepInput] = useState("");

    const [activeTarget, setActiveTarget] = useState<string | null>(null);
    const [tempPipeline, setTempPipeline] = useState<PipelineStep[]>([]);
    const [testInputs, setTestInputs] = useState<Record<string, string>>({});

    const [hiddenFields, setHiddenFields] = useState<Set<string>>(new Set());
    const [showHidden, setShowHidden] = useState(false);
    const [fieldFilter, setFieldFilter] = useState("");

    const bgItem = useColorModeValue("white", "gray.800");
    const borderColorSource = useColorModeValue("emerald.100", "emerald.800");
    const borderColorTarget = useColorModeValue("blue.100", "blue.800");
    const highlightTarget = useColorModeValue("blue.50", "blue.900/30");
    const lineColor = useColorModeValue("rgba(49, 130, 206, 0.45)", "rgba(66, 153, 225, 0.45)");

    useEffect(() => {
        const flatten = (obj: any, prefix = ""): string[] => {
            let paths: string[] = [];
            if (!obj) return paths;
            for (const key in obj) {
                const path = prefix ? `${prefix}.${key}` : key;
                if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
                    paths = [...paths, ...flatten(obj[key], path)];
                } else if (Array.isArray(obj[key]) && obj[key].length > 0 && typeof obj[key][0] === "object") {
                    // Peek into first array element for fields
                    paths = [...paths, ...flatten(obj[key][0], `${path}[0]`)];
                    paths.push(path);
                } else { paths.push(path); }
            }
            return paths;
        };
        if (sampleData) setAvailableFields(flatten(sampleData).sort());
    }, [sampleData]);

    const filteredFields = useMemo(() => {
        return availableFields.filter(f => {
            const matchesFilter = f.toLowerCase().includes(fieldFilter.toLowerCase());
            const isHidden = hiddenFields.has(f);
            return matchesFilter && (showHidden || !isHidden);
        });
    }, [availableFields, fieldFilter, hiddenFields, showHidden]);

    const updateLines = () => {
        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const newLines: any[] = [];
        Object.entries(normalizedMapping).forEach(([target, config]) => {
            const targetEl = document.getElementById(`target-anchor-${target}`);
            if (!targetEl) return;
            config?.sources?.forEach((src, idx) => {
                const sourceEl = document.getElementById(`source-handle-${src.field}`);
                if (sourceEl) {
                    const sRect = sourceEl.getBoundingClientRect();
                    const tRect = targetEl.getBoundingClientRect();
                    newLines.push({
                        x1: sRect.right - containerRect.left,
                        y1: sRect.top + sRect.height / 2 - containerRect.top,
                        x2: tRect.left - containerRect.left,
                        y2: tRect.top + tRect.height / 2 - containerRect.top
                    });
                }
            });
        });
        setLines(newLines);
    };

    useEffect(() => {
        const timeout = setTimeout(updateLines, 100);
        const observer = new ResizeObserver(updateLines);
        if (containerRef.current) observer.observe(containerRef.current);
        window.addEventListener('resize', updateLines);
        return () => { clearTimeout(timeout); observer.disconnect(); window.removeEventListener('resize', updateLines); };
    }, [normalizedMapping, availableFields]);

    const handleMap = (target: string, sourceField: string) => {
        const current = normalizedMapping[target] || { sources: [], pipeline: [] };
        if (current.sources.find(s => s.field === sourceField)) return;
        onChange({ ...normalizedMapping, [target]: { ...current, sources: [...current.sources, { field: sourceField }] } });
    };

    const handleRemoveSource = (target: string, sourceField: string) => {
        const current = normalizedMapping[target];
        if (!current) return;
        const newSources = current.sources.filter(s => s.field !== sourceField);
        onChange({ ...normalizedMapping, [target]: { ...current, sources: newSources } });
    };

    const openWorkshop = (target: string) => {
        setActiveTarget(target);
        setTempPipeline(normalizedMapping[target]?.pipeline || []);

        // Initialize test inputs with sample data or empty strings
        const initialInputs: Record<string, string> = {};
        normalizedMapping[target]?.sources?.forEach(s => {
            const val = s.field.split('.').reduce((o, i) => (o ? o[i] : undefined), sampleData);
            initialInputs[s.field] = val ? String(val) : "";
        });
        setTestInputs(initialInputs);
        onOpen();
    };

    const openStepTester = (stepId: string) => {
        setActiveStepId(stepId);
        const step = tempPipeline.find(s => s.id === stepId);
        setManualStepInput(step?.type === 'ADDRESS' ? "5-111 Rue Peel, Montreal" : "Sample Value");
        setIsTesterOpen(true);
    };

    const closeStepTester = () => {
        setIsTesterOpen(false);
        setActiveStepId(null);
    };

    const runStepLogic = (type: string, params: any, input: any) => {
        let current = input;
        switch (type) {
            case 'UPPER': current = String(current).toUpperCase(); break;
            case 'LOWER': current = String(current).toLowerCase(); break;
            case 'TITLE': current = String(current).replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()); break;
            case 'TRIM': current = String(current).trim(); break;
            case 'REPLACE': current = String(current).split(params.from || '').join(params.to || ''); break;
            case 'SPLIT': {
                const parts = String(current).split(params.char || '@');
                current = parts[params.index || 0] || "";
                break;
            }
            case 'SUBSTRING': {
                const start = Number(params.start || 0);
                const len = Number(params.len || 5);
                current = String(current).substr(start, len);
                break;
            }
            case 'CONSTANT': current = params.value; break;
            case 'ADDRESS': {
                const s = String(current).toLowerCase().normalize("NFC").replace(/,/g, " ").replace(/\s+/g, " ").trim();
                let unit = "";
                let streetNumber = "";

                // Strategy 1: Dash Pattern (Unit-Number, e.g., 5-111 Rue Peel)
                const dashMatch = s.match(/\b([a-z0-9]+)\s*-\s*(\d+)\s+[\p{L}]/iu);
                if (dashMatch) {
                    unit = dashMatch[1].toUpperCase();
                    streetNumber = dashMatch[2];
                } else {
                    // Strategy 2: Keywords (English & French) - e.g., Suite 101, Unite 6
                    const unitRE = /\b(?:unit|unite|unité|apt|apartment|suite|ste|#|app|appartement|logement|bureau)\s*[:#\-\/]*\s*([a-z0-9]+)\b/iu;
                    const unitMatch = s.match(unitRE);
                    if (unitMatch) unit = unitMatch[1].toUpperCase();

                    // Strategy 3: Street Number (Digit anchored by street types) - e.g., 111 Rue Peel
                    const streetRE = /\b(\d+)\s+(?:[\p{L}]+\s+)*(?:st|street|rd|road|ave|avenue|blvd|boulevard|rue|chemin|ch|route|côte|boul|av|allée|impasse)\b/iu;
                    const streetMatch = s.match(streetRE);
                    if (streetMatch) streetNumber = streetMatch[1];
                }

                if (params.part === 'unit') {
                    current = unit;
                } else if (params.part === 'streetNumber') {
                    current = streetNumber;
                } else {
                    // Extract Street Name: remove number and unit
                    let name = String(input).normalize("NFC");
                    if (unit) {
                        const uRE = new RegExp(`\\b(?:unit|unite|unité|apt|apartment|suite|ste|#|app|appartement|logement|bureau)\\s*[:#\\-\\/]*\\s*${unit}\\b`, 'iu');
                        name = name.replace(uRE, "");
                    }
                    if (streetNumber) {
                        name = name.replace(new RegExp(`\\b${streetNumber}\\b`, 'g'), "");
                    }
                    if (dashMatch) {
                        name = name.replace(new RegExp(`${dashMatch[1]}\\s*-\\s*${dashMatch[2]}`, 'iu'), "");
                    }
                    // Remove city/province (usually after comma)
                    name = name.split(',')[0];
                    current = name.replace(/[.#-]/g, " ").replace(/\s+/g, " ").trim();
                }
                break;
            }
        }
        return current;
    };

    const addStep = (type: PipelineStep['type']) => {
        const newStep: PipelineStep = {
            id: Math.random().toString(36).substr(2, 9),
            type,
            params: type === 'SPLIT' ? { char: '@', index: 0 } :
                type === 'CONCAT' ? { char: ' ', mode: 'all' } :
                    type === 'CONSTANT' ? { value: '' } :
                        type === 'MATH' ? { op: '+', mode: 'constant', val: 0, sourceIdx: 1 } :
                            type === 'SUBSTRING' ? { start: 0, len: 5 } :
                                type === 'ADDRESS' ? { part: 'streetNumber' } :
                                    type === 'DATE_FORMAT' ? { format: 'YYYY-MM-DD' } : {}
        };
        setTempPipeline([...tempPipeline, newStep]);
    };

    const updateStep = (id: string, params: any) => {
        setTempPipeline(tempPipeline.map(s => s.id === id ? { ...s, params: { ...s.params, ...params } } : s));
    };

    const removeStep = (id: string) => {
        setTempPipeline(tempPipeline.filter(s => s.id !== id));
    };

    const simulatePipeline = () => {
        if (!activeTarget) return "None";
        const config = normalizedMapping[activeTarget];

        let current: any = null;

        // Initial Input
        const getValue = (path: string) => {
            // Use test inputs if available, otherwise fallback to sampleData
            if (testInputs[path] !== undefined) return testInputs[path];
            const val = path.split('.').reduce((o, i) => (o ? o[i] : undefined), sampleData);
            return val;
        };

        const constantStep = tempPipeline.find(s => s.type === 'CONSTANT');
        if (constantStep) {
            current = constantStep.params.value;
        } else if (config.sources.length > 0) {
            // Important: Start with a readable string or a single value if possible
            const values = config.sources.map(s => getValue(s.field));
            current = values.length === 1 ? values[0] : values;
        }

        // Apply Steps
        tempPipeline.forEach(step => {
            if (!current && step.type !== 'CONSTANT') return;
            switch (step.type) {
                case 'UPPER': current = String(current).toUpperCase(); break;
                case 'LOWER': current = String(current).toLowerCase(); break;
                case 'TRIM': current = String(current).trim(); break;
                case 'TITLE': current = String(current).replace(/\b\w/g, l => l.toUpperCase()); break;
                case 'SPLIT': current = String(current).split(step.params.char)[step.params.index] || ""; break;
                case 'CONCAT': {
                    const sources = config.sources.map(s => getValue(s.field));
                    if (step.params.mode === 'all') {
                        current = sources.join(step.params.char);
                    } else if (step.params.indices) {
                        current = step.params.indices.map((i: number) => sources[i]).join(step.params.char);
                    }
                    break;
                }
                case 'MATH': {
                    const v1 = Number(current);
                    let v2 = 0;
                    if (step.params.mode === 'source' && config.sources[step.params.sourceIdx]) {
                        v2 = Number(getValue(config.sources[step.params.sourceIdx].field));
                    } else {
                        v2 = Number(step.params.val);
                    }

                    if (step.params.op === '+') current = v1 + v2;
                    if (step.params.op === '-') current = v1 - v2;
                    if (step.params.op === '*') current = v1 * v2;
                    if (step.params.op === '/') current = v1 / v2;
                    break;
                }
                case 'CONSTANT': current = step.params.value; break;
                case 'SUBSTRING': {
                    const start = Number(step.params.start);
                    const len = Number(step.params.len);
                    current = String(current).substr(start, len);
                    break;
                }
                case 'DATE_FORMAT': current = "Formatted Date Placeholder"; break;
                case 'ADDRESS': {
                    const s = String(current).toLowerCase().normalize("NFC").replace(/,/g, " ").replace(/\s+/g, " ").trim();
                    let unit = "";
                    let streetNumber = "";

                    // Strategy 1: Dash Pattern (Unit-Number, e.g., 5-111 Rue Peel)
                    const dashMatch = s.match(/\b([a-z0-9]+)\s*-\s*(\d+)\s+[\p{L}]/iu);
                    if (dashMatch) {
                        unit = dashMatch[1].toUpperCase();
                        streetNumber = dashMatch[2];
                    } else {
                        // Strategy 2: Keywords (English & French)
                        const unitRE = /\b(?:unit|unite|unité|apt|apartment|suite|ste|#|app|appartement|logement|bureau)\s*[:#\-\/]*\s*([a-z0-9]+)\b/iu;
                        const unitMatch = s.match(unitRE);
                        if (unitMatch) unit = unitMatch[1].toUpperCase();

                        // Strategy 3: Street Number (Digit anchored by street types)
                        const streetRE = /\b(\d+)\s+(?:[\p{L}]+\s+)*(?:st|street|rd|road|ave|avenue|blvd|boulevard|rue|chemin|ch|route|côte|boul|av|allée|impasse)\b/iu;
                        const streetMatch = s.match(streetRE);
                        if (streetMatch) streetNumber = streetMatch[1];
                    }

                    if (step.params.part === 'unit') {
                        current = unit;
                    } else if (step.params.part === 'streetNumber') {
                        current = streetNumber;
                    } else {
                        // Extract Street Name: remove number and unit
                        let name = String(current).normalize("NFC");
                        if (unit) {
                            const uRE = new RegExp(`\\b(?:unit|unite|unité|apt|apartment|suite|ste|#|app|appartement|logement|bureau)\\s*[:#\\-\\/]*\\s*${unit}\\b`, 'iu');
                            name = name.replace(uRE, "");
                        }
                        if (streetNumber) {
                            name = name.replace(new RegExp(`\\b${streetNumber}\\b`, 'g'), "");
                        }
                        if (dashMatch) {
                            name = name.replace(new RegExp(`${dashMatch[1]}\\s*-\\s*${dashMatch[2]}`, 'iu'), "");
                        }
                        name = name.split(',')[0];
                        current = name.replace(/[.#-]/g, " ").replace(/\s+/g, " ").trim();
                    }
                    break;
                }
            }
        });

        if (current === null || current === undefined) return "";
        if (Array.isArray(current)) return `[ ${current.map(v => typeof v === 'object' ? JSON.stringify(v) : String(v)).join(', ')} ]`;
        if (typeof current === 'object') return JSON.stringify(current);
        return String(current);
    };

    const autoMatch = () => {
        const newMapping = { ...normalizedMapping };
        targetFields.forEach(target => {
            // Already matched?
            if (newMapping[target]?.sources?.length > 0) return;

            // Simple fuzzy match: exact or contains (case-insensitive)
            const match = availableFields.find(src => {
                const s = src.toLowerCase().split('.').pop() || "";
                const t = target.toLowerCase();
                return s === t || t.includes(s) || s.includes(t);
            });

            if (match) {
                newMapping[target] = { ...newMapping[target], sources: [{ field: match }] };
            }
        });
        onChange(newMapping);
    };

    const savePipeline = () => {
        if (!activeTarget) return;
        const current = normalizedMapping[activeTarget];
        onChange({ ...normalizedMapping, [activeTarget]: { ...current, pipeline: tempPipeline } });
        onClose();
    };

    const ops = [
        { type: 'UPPER', icon: FiType, label: 'Uppercase', desc: 'ALL CAPS' },
        { type: 'LOWER', icon: FiType, label: 'Lowercase', desc: 'all caps' },
        { type: 'TITLE', icon: FiType, label: 'Title Case', desc: 'Proper Case' },
        { type: 'ADDRESS', icon: FiMap, label: 'Extract Addr', desc: 'Unit & St Num' },
        { type: 'SPLIT', icon: FiScissors, label: 'Split/Extract', desc: 'Pull parts out' },
        { type: 'CONCAT', icon: FiAlignCenter, label: 'Join Items', desc: 'Combine sources' },
        { type: 'MATH', icon: FiHash, label: 'Math', desc: 'Calculate' },
        { type: 'CONSTANT', icon: FiZap, label: 'Fixed Val', desc: 'Assign static' },
        { type: 'SUBSTRING', icon: FiScissors, label: 'Clip', desc: 'Take chars' },
        { type: 'DATE_FORMAT', icon: FiClock, label: 'Date', desc: 'Format time' },
    ] as const;

    const opDocs: Record<string, { desc: string, ex: string }> = {
        SPLIT: { desc: "Divide a string by a character and pick a specific piece.", ex: "Email: 'john@gmail.com' → Split by '@', Index 0 → Result: 'john'" },
        CONCAT: { desc: "Joins linked source fields into one string. You can join all or pick specific ones.", ex: "Fields: [House, Street] → Join with ' ' → Result: '123 Main St'" },
        MATH: { desc: "Calculates values using either a fixed number or another source field.", ex: "Price: 100 → Operation: + Tax (Source 1) → Result: 115" },
        CONSTANT: { desc: "Overrides input with a fixed value of your choice.", ex: "Input: 'Anything' → Value: 'USA' → Result: 'USA'" },
        DATE_FORMAT: { desc: "Converts dates into your preferred format.", ex: "Input: '2023-12-01' → Format: 'DD/MM/YYYY' → Result: '01/12/2023'" },
        SUBSTRING: { desc: "Extracts a portion of a string starting at an index for a specific length.", ex: "Input: 'ORDER_555' → Start: 6, Len: 3 → Result: '555'" },
        ADDRESS: { desc: "Bilingual (EN/FR) parsing for Canadian Unit and Street numbers. Handles 'unite', 'unité', 'bureau', etc.", ex: "Input: '111 Rue Peel unite 6' → Mode: Unit → Result: '6'" }
    };

    return (
        <Box w="full" position="relative" ref={containerRef} onDragOver={(e) => { e.preventDefault(); setMousePos({ x: e.clientX, y: e.clientY }); }}>
            {/* SVG OVERLAY */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 10 }}>
                {lines.map((line, i) => {
                    const cx = (line.x1 + line.x2) / 2;
                    return <path key={i} d={`M ${line.x1} ${line.y1} C ${cx} ${line.y1}, ${cx} ${line.y2}, ${line.x2} ${line.y2}`} stroke={lineColor} strokeWidth="1.5" fill="none" strokeDasharray="3 2" />;
                })}
                {draggingField && containerRef.current && (() => {
                    const sourceEl = document.getElementById(`source-handle-${draggingField}`);
                    if (!sourceEl) return null;
                    const containerRect = containerRef.current.getBoundingClientRect();
                    const sRect = sourceEl.getBoundingClientRect();
                    const x1 = sRect.right - containerRect.left;
                    const y1 = sRect.top + sRect.height / 2 - containerRect.top;
                    const x2 = mousePos.x - containerRect.left;
                    const y2 = mousePos.y - containerRect.top;
                    const cx = (x1 + x2) / 2;
                    return <path d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`} stroke="#3182ce" strokeWidth="2" fill="none" strokeDasharray="5 3" />;
                })()}
            </svg>

            <Flex mb={6} justify="space-between" align="center">
                <HStack spacing={3}>
                    <Icon as={FiMap as any} color="blue.500" boxSize={4} />
                    <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" fontSize="md">Visual Field Mapper</Text>
                        <Text fontSize="10px" color="gray.500">Connect RMS data to API endpoints</Text>
                    </VStack>
                </HStack>
                <Button size="xs" leftIcon={<Icon as={FiRefreshCw as any} />} onClick={autoMatch} variant="solid" colorScheme="blue" borderRadius="lg" px={4}>Auto-suggest</Button>
            </Flex>

            <Flex gap="320px" justify="center" position="relative" zIndex={2}>
                {/* SOURCE COLUMN */}
                <VStack align="stretch" spacing={3} w="320px">
                    <VStack align="start" spacing={1} mb={2}>
                        <HStack px={2}>
                            <Icon as={FiDatabase as any} color="emerald.500" boxSize={3} />
                            <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">Source (RMS Data)</Text>
                        </HStack>
                        <HStack w="full" spacing={2}>
                            <Select
                                size="sm"
                                borderRadius="lg"
                                variant="filled"
                                value={sampleSource}
                                onChange={(e) => onSampleSourceChange(e.target.value as any)}
                                flex={1}
                            >
                                <option value="mock">MOCK DATA (Default)</option>
                                <option value="online_static">ONLINE ORDER (Static)</option>
                                <option value="online_live">ONLINE ORDER (Live)</option>
                                <option value="local_static">LOCAL DELIVERY (Static)</option>
                                <option value="local_live">LOCAL DELIVERY (Live)</option>
                            </Select>
                            <IconButton
                                size="sm"
                                aria-label="Toggle hidden"
                                icon={<Icon as={showHidden ? FiEye : FiEyeOff as any} />}
                                colorScheme={showHidden ? "blue" : "gray"}
                                variant="ghost"
                                onClick={() => setShowHidden(!showHidden)}
                            />
                        </HStack>
                        <InputGroup size="xs" mt={2}>
                            <InputLeftElement pointerEvents="none"><Icon as={FiSearch as any} color="gray.400" /></InputLeftElement>
                            <Input
                                placeholder="Search fields..."
                                borderRadius="md"
                                value={fieldFilter}
                                onChange={(e) => setFieldFilter(e.target.value)}
                            />
                        </InputGroup>
                    </VStack>

                    <VStack
                        align="stretch"
                        spacing={1.5}
                        maxH="700px"
                        overflowY="auto"
                        pr={1}
                        sx={{
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none'
                        }}
                    >
                        {filteredFields.map((field) => (
                            <HStack key={field} p={2} bg={bgItem} borderRadius="md" border="1px solid" borderColor={borderColorSource} spacing={2} cursor="grab" draggable onDragStart={(e) => { e.dataTransfer.setData("sourceField", field); setDraggingField(field); }} onDragEnd={() => setDraggingField(null)} boxShadow="xs" _hover={{ transform: "scale(1.02)", boxShadow: "sm" }} transition="all 0.2s">
                                <Box id={`source-handle-${field}`} p={1} borderRadius="sm" bg="emerald.50" _dark={{ bg: "emerald.900/40" }}><Icon as={FiMove as any} boxSize={3} color="emerald.600" /></Box>
                                <VStack align="start" spacing={0} flex={1}>
                                    <Text fontSize="xs" fontWeight="bold" isTruncated>{field}</Text>
                                </VStack>
                                <IconButton
                                    size="xs"
                                    aria-label="Hide field"
                                    icon={<Icon as={hiddenFields.has(field) ? FiEyeOff : FiEye as any} />}
                                    variant="ghost"
                                    colorScheme={hiddenFields.has(field) ? "red" : "gray"}
                                    onClick={() => {
                                        const next = new Set(hiddenFields);
                                        if (next.has(field)) next.delete(field);
                                        else next.add(field);
                                        setHiddenFields(next);
                                    }}
                                />
                            </HStack>
                        ))}
                    </VStack>
                </VStack>

                {/* TARGET COLUMN */}
                <VStack align="stretch" spacing={3} w="320px">
                    <VStack align="start" spacing={1} mb={2}>
                        <HStack px={2}>
                            <Icon as={FiChevronRight as any} color="blue.500" boxSize={3} />
                            <Text fontSize="10px" fontWeight="bold" color="gray.500" textTransform="uppercase">Target ({apiName === 'LivroNuage' ? 'Uber Direct' : apiName} API)</Text>
                        </HStack>
                        <Select
                            size="sm"
                            borderRadius="lg"
                            variant="filled"
                            value={apiName}
                            onChange={(e) => onApiChange(e.target.value)}
                        >
                            {availableApis.map(api => (
                                <option key={api} value={api}>
                                    {api === 'LivroNuage' ? 'UBER DIRECT' : api.toUpperCase()}
                                </option>
                            ))}
                        </Select>
                    </VStack>

                    <VStack
                        align="stretch"
                        spacing={1.5}
                        maxH="700px"
                        overflowY="auto"
                        pr={1}
                        sx={{
                            '&::-webkit-scrollbar': { display: 'none' },
                            msOverflowStyle: 'none',
                            scrollbarWidth: 'none'
                        }}
                    >
                        {targetFields.map((field) => {
                            const config = normalizedMapping[field];
                            const hasLogic = config?.pipeline?.length > 0;
                            return (
                                <HStack key={field} p={2} bg={bgItem} borderRadius="md" border="1px solid" borderColor={config?.sources?.length || hasLogic ? "blue.400" : borderColorTarget} spacing={2} position="relative" onDrop={(e) => { e.preventDefault(); const src = e.dataTransfer.getData("sourceField"); if (src) handleMap(field, src); setDraggingField(null); }} onDragOver={(e) => e.preventDefault()} _hover={{ boxShadow: "sm" }}>
                                    <Box id={`target-anchor-${field}`} position="absolute" left="-3px" top="50%" w="6px" h="6px" bg="blue.400" borderRadius="full" zIndex={11} mt="-3px" />
                                    <Box flex={1}>
                                        <HStack justify="space-between">
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="xs" fontWeight="bold" color={config?.sources?.length || hasLogic ? "blue.700" : "gray.600"} isTruncated>{field}</Text>
                                            </VStack>
                                            <IconButton aria-label="Workshop" size="xs" variant={hasLogic ? "solid" : "ghost"} colorScheme={hasLogic ? "blue" : "gray"} icon={<Icon as={FiCpu as any} />} onClick={() => openWorkshop(field)} h="20px" w="20px" minW="20px" />
                                        </HStack>
                                        <Flex wrap="wrap" gap={1} mt={config?.sources?.length ? 1 : 0}>
                                            {config?.sources?.map((src) => (
                                                <Tag key={src.field} size="sm" borderRadius="sm" variant="subtle" colorScheme="blue" h="16px" py={0} px={1.5}>
                                                    <TagLabel fontSize="8px" fontWeight="bold">{src.field}</TagLabel>
                                                    <TagCloseButton onClick={() => handleRemoveSource(field, src.field)} boxSize={2} />
                                                </Tag>
                                            ))}
                                        </Flex>
                                    </Box>
                                </HStack>
                            );
                        })}
                    </VStack>
                </VStack>
            </Flex>

            {/* PIPELINE WORKSHOP MODAL */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="5xl">
                <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.700" />
                <ModalContent borderRadius="3xl" overflow="hidden" boxShadow="dark-lg" minH="700px">
                    <ModalHeader bg="blue.600" color="white" py={3}>
                        <HStack justify="space-between">
                            <HStack><Icon as={FiCpu as any} /><Text fontSize="md">Pipeline Workshop: {activeTarget}</Text></HStack>
                            <Badge colorScheme="blue" variant="solid" px={3} borderRadius="full">SECURE ENGINE</Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" top={3} />
                    <ModalBody p={0} display="flex">
                        {/* Sidebar: Available Operations */}
                        <Box w="300px" bg="gray.50" _dark={{ bg: "gray.900" }} p={6} borderRight="1px solid" borderColor="gray.200">
                            <VStack align="stretch" spacing={6}>
                                <VStack align="start" spacing={1}>
                                    <Text fontSize="10px" fontWeight="black" color="blue.500" letterSpacing="tighter">ADD TRANSFORMATION</Text>
                                    <Text fontSize="11px" color="gray.500">Chain logic to process your data</Text>
                                </VStack>
                                <Grid templateColumns="repeat(1, 1fr)" gap={3}>
                                    {ops.map(op => (
                                        <HStack
                                            key={op.type}
                                            p={3}
                                            bg="white"
                                            _dark={{ bg: "gray.800" }}
                                            borderRadius="xl"
                                            boxShadow="sm"
                                            cursor="pointer"
                                            border="1px solid"
                                            borderColor="gray.100"
                                            onClick={() => addStep(op.type as any)}
                                            _hover={{ borderColor: "blue.400", transform: "translateX(4px)" }}
                                            transition="all 0.2s"
                                            position="relative"
                                        >
                                            <Box p={2} borderRadius="lg" bg="blue.50" color="blue.500"><Icon as={op.icon as any} boxSize={4} /></Box>
                                            <VStack align="start" spacing={0} flex={1}>
                                                <Text fontSize="xs" fontWeight="bold">{op.label}</Text>
                                                <Text fontSize="9px" color="gray.400">{op.desc}</Text>
                                            </VStack>

                                        </HStack>
                                    ))}
                                </Grid>
                            </VStack>
                        </Box>

                        {/* Workspace: The Pipeline */}
                        <Box flex={1} p={8} bg="white" _dark={{ bg: "gray.800" }}>
                            <VStack align="stretch" spacing={4}>
                                <HStack justify="space-between">
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="lg" fontWeight="bold">Pipeline Sequence</Text>
                                        <Text fontSize="xs" color="gray.500">Steps are executed from first to last</Text>
                                    </VStack>
                                    <Box p={4} bg="blue.50" _dark={{ bg: "blue.900/20" }} borderRadius="2xl" border="1px dashed" borderColor="blue.300" minW="280px">
                                        <HStack justify="space-between" align="center">
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Text fontSize="9px" fontWeight="bold" color="blue.400" textTransform="uppercase">Live Transformation Result</Text>
                                                <Text fontSize="md" fontWeight="black" color="blue.700" _dark={{ color: "blue.200" }} wordBreak="break-word">{simulatePipeline()}</Text>
                                            </VStack>
                                        </HStack>
                                    </Box>
                                </HStack>

                                <Divider my={4} />

                                <VStack align="stretch" spacing={3} maxH="550px" overflowY="visible" pr={2}>
                                    {tempPipeline.length === 0 && (
                                        <Flex direction="column" align="center" justify="center" h="200px" opacity={0.4}>
                                            <Icon as={FiZap as any} boxSize={8} mb={2} />
                                            <Text fontWeight="bold">No logic steps added yet</Text>
                                            <Text fontSize="xs">Pick an operation from the left to start</Text>
                                        </Flex>
                                    )}
                                    {tempPipeline.map((step, index) => (
                                        <Box key={step.id} p={4} bg="gray.25" _dark={{ bg: "gray.700/50" }} borderRadius="2xl" border="1px solid" borderColor="gray.100" position="relative">
                                            <HStack spacing={4}>
                                                <Badge colorScheme="gray" variant="solid" borderRadius="full" h="20px" w="20px" textAlign="center" lineHeight="20px">{index + 1}</Badge>
                                                <Text fontWeight="black" fontSize="xs" w="100px">{step.type}</Text>

                                                <HStack flex={1} spacing={4}>
                                                    {step.type === 'SPLIT' && (
                                                        <>
                                                            <FormControl size="xs"><FormLabel fontSize="10px">Char</FormLabel><Input size="xs" value={step.params.char} onChange={e => updateStep(step.id, { char: e.target.value })} /></FormControl>
                                                            <FormControl size="xs"><FormLabel fontSize="10px">Index</FormLabel><Input size="xs" type="number" value={step.params.index} onChange={e => updateStep(step.id, { index: parseInt(e.target.value) })} /></FormControl>
                                                        </>
                                                    )}
                                                    {step.type === 'CONCAT' && (
                                                        <>
                                                            <FormControl size="xs" w="140px"><FormLabel fontSize="10px">Merge Mode</FormLabel><Select size="xs" value={step.params.mode} onChange={e => updateStep(step.id, { mode: e.target.value })} borderRadius="md"><option value="all">Join All Sources</option><option value="subset">Subset Index</option></Select></FormControl>
                                                            <FormControl size="xs" w="100px"><FormLabel fontSize="10px">Separator</FormLabel><Input size="xs" value={step.params.char} placeholder="e.g. , " onChange={e => updateStep(step.id, { char: e.target.value })} borderRadius="md" /></FormControl>
                                                            <VStack align="start" spacing={0} flex={1} bg="blue.50" _dark={{ bg: "blue.900/20" }} p={2} borderRadius="lg">
                                                                <Text fontSize="8px" fontWeight="black" color="blue.500">CONTEXT: LINKED DATA</Text>
                                                                <Text fontSize="10px" color="blue.700" _dark={{ color: "blue.200" }} fontWeight="bold" isTruncated>
                                                                    {(normalizedMapping[activeTarget || '']?.sources || []).map(s => s.field).join(' + ') || "No fields linked"}
                                                                </Text>
                                                            </VStack>
                                                        </>
                                                    )}
                                                    {step.type === 'CONSTANT' && <FormControl size="xs"><FormLabel fontSize="10px">Value</FormLabel><Input size="xs" value={step.params.value} onChange={e => updateStep(step.id, { value: e.target.value })} borderRadius="md" /></FormControl>}
                                                    {step.type === 'MATH' && (
                                                        <>
                                                            <Select size="xs" w="100px" value={step.params.mode} onChange={e => updateStep(step.id, { mode: e.target.value })} borderRadius="md"><option value="constant">Fixed Num</option><option value="source">Source Field</option></Select>
                                                            <Select size="xs" w="60px" value={step.params.op} onChange={e => updateStep(step.id, { op: e.target.value })} borderRadius="md"><option value="+">+</option><option value="-">-</option><option value="*">*</option><option value="/">/</option></Select>
                                                            {step.params.mode === 'constant' ? (
                                                                <Input size="xs" w="80px" type="number" value={step.params.val} onChange={e => updateStep(step.id, { val: parseFloat(e.target.value) })} borderRadius="md" />
                                                            ) : (
                                                                <Select size="xs" flex={1} value={step.params.sourceIdx} onChange={e => updateStep(step.id, { sourceIdx: parseInt(e.target.value) })} borderRadius="md">
                                                                    {(normalizedMapping[activeTarget || '']?.sources || []).map((s, i) => (
                                                                        <option key={i} value={i}>{s.field}</option>
                                                                    ))}
                                                                </Select>
                                                            )}
                                                        </>
                                                    )}
                                                    {step.type === 'SUBSTRING' && (
                                                        <>
                                                            <FormControl size="xs"><FormLabel fontSize="10px">Start</FormLabel><Input size="xs" type="number" value={step.params.start} onChange={e => updateStep(step.id, { start: parseInt(e.target.value) })} /></FormControl>
                                                            <FormControl size="xs"><FormLabel fontSize="10px">Length</FormLabel><Input size="xs" type="number" value={step.params.len} onChange={e => updateStep(step.id, { len: parseInt(e.target.value) })} /></FormControl>
                                                        </>
                                                    )}
                                                    {step.type === 'ADDRESS' && (
                                                        <FormControl size="xs" w="200px">
                                                            <FormLabel fontSize="10px">Pick Result Part</FormLabel>
                                                            <Select size="xs" value={step.params.part} onChange={e => updateStep(step.id, { part: e.target.value })} borderRadius="md">
                                                                <option value="unit">Unit Number (Appartement)</option>
                                                                <option value="streetNumber">Street Number (Numéro civique)</option>
                                                                <option value="streetName">Street Name (Nom de rue)</option>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                    {['UPPER', 'LOWER', 'TITLE', 'TRIM'].includes(step.type) && <Text fontSize="xs" color="gray.500 italic">No configuration needed</Text>}
                                                </HStack>

                                                <HStack>
                                                    <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<Icon as={FiPlay as any} />} onClick={() => openStepTester(step.id)} borderRadius="md">Test Result</Button>
                                                    <IconButton aria-label="Remove" size="xs" colorScheme="red" variant="ghost" icon={<Icon as={FiX as any} />} onClick={() => removeStep(step.id)} />
                                                </HStack>
                                            </HStack>
                                        </Box>
                                    ))}
                                </VStack>
                            </VStack>
                        </Box>
                    </ModalBody>
                    <ModalFooter bg="gray.50" py={4} borderTop="1px solid" borderColor="gray.100">
                        <Button variant="ghost" mr={4} onClick={() => { setTempPipeline([]); onClose(); }} size="sm" colorScheme="red">Clear All Logic</Button>
                        <Button colorScheme="blue" onClick={savePipeline} leftIcon={<Icon as={FiPlay as any} />} size="md" borderRadius="xl" px={10} boxShadow="lg">Compile & Save Pipeline</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {/* STEP TESTER MODAL (Combined Help & Simulator) */}
            <Modal isOpen={isTesterOpen} onClose={closeStepTester} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(8px)" />
                <ModalContent borderRadius="2xl" p={0} overflow="hidden">
                    <ModalHeader bg="gray.900" color="white" fontSize="sm" py={3}>
                        <HStack spacing={2}>
                            <Icon as={FiCode as any} color="blue.400" />
                            <Text>Step Simulator: {activeStepId && tempPipeline.find(s => s.id === activeStepId)?.type}</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color="white" />
                    <ModalBody p={6}>
                        {activeStepId && (
                            <VStack align="stretch" spacing={6}>
                                {/* Documentation Section */}
                                <Box bg="blue.50" _dark={{ bg: "blue.900/20" }} p={4} borderRadius="xl" border="1px solid" borderColor="blue.100">
                                    <Text fontWeight="black" fontSize="xs" color="blue.600" mb={1} textTransform="uppercase" letterSpacing="wider">Operation Guide</Text>
                                    <Text fontSize="sm" color="gray.700" _dark={{ color: "gray.300" }} mb={3}>
                                        {opDocs[tempPipeline.find(s => s.id === activeStepId)?.type!]?.desc}
                                    </Text>
                                    <VStack align="start" spacing={1}>
                                        <Text fontSize="9px" fontWeight="bold" color="blue.400">EXAMPLE</Text>
                                        <Code colorScheme="blue" variant="solid" w="full" p={2} borderRadius="md" fontSize="10px">
                                            {opDocs[tempPipeline.find(s => s.id === activeStepId)?.type!]?.ex}
                                        </Code>
                                    </VStack>
                                </Box>

                                {/* Live Tester Section */}
                                <VStack align="stretch" spacing={4}>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="bold">Simulate Input Value</FormLabel>
                                        <Input
                                            placeholder="Type a value to test..."
                                            value={manualStepInput}
                                            onChange={(e) => setManualStepInput(e.target.value)}
                                            size="md"
                                            borderRadius="xl"
                                            bg="gray.50"
                                            border="2px solid"
                                            borderColor="gray.100"
                                            _focus={{ borderColor: "blue.400", bg: "white" }}
                                        />
                                    </FormControl>

                                    <Box p={5} bg="gray.900" borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100" shadow="xl">
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="10px" fontWeight="black" color="blue.400" textTransform="uppercase" letterSpacing="widest">Live Transformer Output</Text>
                                            <Badge colorScheme="green" variant="subtle" fontSize="9px">Active</Badge>
                                        </HStack>
                                        <Text fontSize="lg" fontWeight="black" color="white" fontFamily="mono">
                                            {(() => {
                                                const step = tempPipeline.find(s => s.id === activeStepId);
                                                if (!step) return "";
                                                const result = runStepLogic(step.type, step.params, manualStepInput);
                                                return typeof result === 'object' ? JSON.stringify(result) : String(result);
                                            })()}
                                        </Text>
                                    </Box>
                                </VStack>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100" py={3}>
                        <Button colorScheme="blue" onClick={closeStepTester} w="full" borderRadius="xl">Done Testing</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default FieldMappingTool;
